import type { ProductDetail } from 'src/models/product/schema';
import { BackendApiFetch } from 'src/utils/backendApi';
import { Left, Right } from 'src/utils/either';
import { errorMessage } from 'src/utils/errorMessage';
import { shoppingApiBaseUrl } from 'src/utils/shoppingApiBaseUrl';
import { getLocaleFromUrl } from 'src/utils/subdomain';
import { getRequest } from '../wa/gateways/api/wamazingApi';
import type { ApiResponse } from './response/ApiResponse';

const fetchSearchProducts = async (query: string, shopId?: number): Promise<ApiResponse<ProductDetail[]>> => {
  const path = shopId ? `/api/v1/search?q=${query}&shopId=${shopId}` : `/api/v1/search?q=${query}`;
  const response = await getRequest<ProductDetail[] | { errors: string[] }>({
    baseURI: shoppingApiBaseUrl(getLocaleFromUrl()),
    path
  });
  if ('errors' in response && response.errors.length > 0) return new Left({ errors: response.errors });
  return new Right(response as ProductDetail[]);
};

const fetchPopularSearchKeywords = async (): Promise<string[]> => {
  const locale = getLocaleFromUrl();
  const path = '/api/v1/popular_search_keywords';
  try {
    const response = await BackendApiFetch<string[]>({
      path,
      localeType: locale
    });

    if (response.statusCode !== 200) {
      throw new Error('Failed to fetch popular search keywords');
    }

    return response.data;
  } catch (e) {
    throw new Error(errorMessage(e));
  }
};

const fetchAutoCompletionKeywords = async (keyword: string): Promise<string[]> => {
  const locale = getLocaleFromUrl();
  const path = `/api/v1/auto_completion_keywords?q=${encodeURIComponent(keyword)}`;
  try {
    const response = await BackendApiFetch<string[]>({
      path,
      localeType: locale
    });

    if (response.statusCode !== 200) {
      throw new Error('Failed to fetch auto completion keywords');
    }

    return response.data;
  } catch (e) {
    throw new Error(errorMessage(e));
  }
};

export { fetchAutoCompletionKeywords, fetchPopularSearchKeywords, fetchSearchProducts };
